// 201 -- shared notification row + deep-link resolution, used by both the
// desktop bell popover and the mobile /notifications screen so the two never
// drift.
import type { Notification } from "@/hooks/useNotifications";

/** Resolve a notification's tap target from its payload. Returns null when the
 *  payload carries no known destination -- the row stays tap-inert (mark-read
 *  only); we never invent targets. */
export function getNotificationPath(notification: Notification): string | null {
  const data = notification.data;
  if (!data) return null;

  // 197 event board posts land on the Board tab, not the event root.
  if (data.type === "event_post" && data.event_id) return `/events/${data.event_id}?tab=board`;
  // 188 enrollment requests: the coach approval card lives on the dashboard.
  if (data.type === "enrollment_request") return "/";
  if (data.event_id) return `/events/${data.event_id}`;
  if (data.message_id) return "/messages";
  return null;
}

export function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffSec = Math.floor((now - then) / 1000);

  if (diffSec < 60) return "just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export function NotificationItem({
  notification,
  onClick,
}: {
  notification: Notification;
  onClick: (notification: Notification) => void;
}) {
  const isUnread = !notification.read_at;

  return (
    <button
      type="button"
      data-notification-id={notification.id}
      data-unread={isUnread ? "true" : "false"}
      onClick={() => onClick(notification)}
      className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted ${
        isUnread ? "bg-primary/5" : ""
      }`}
    >
      {/* Unread indicator dot */}
      <div className="mt-1.5 flex h-2 w-2 shrink-0">
        {isUnread && <span className="h-2 w-2 rounded-full bg-primary" />}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className={`truncate text-sm ${isUnread ? "font-medium text-foreground" : "text-muted-foreground"}`}>
          {notification.title}
        </span>
        <span className="line-clamp-2 text-xs text-muted-foreground">
          {notification.body}
        </span>
        <span className="text-xs text-muted-foreground/60">
          {timeAgo(notification.created_at)}
        </span>
      </div>
    </button>
  );
}
